import { useEffect, useRef, useState } from "react";

// Scroll-spy for the vertical topic list. Watches the on-page topic sections
// (elements with id `topic-<id>`) and reports which one the viewer is currently
// looking at, plus a smooth scroll-to helper for the topic navigator.
//
// `offset` is how far below the viewport top a section's top must pass before
// it counts as "active" — set it to clear any sticky header above the list.
export function useActiveTopic(topicIds: string[], offset = 140) {
  const key = topicIds.join("|");
  const [activeId, setActiveId] = useState<string | undefined>(topicIds[0]);
  // While a click-triggered smooth scroll animates, pin the highlight to the
  // target so it doesn't flicker through the topics the scroll passes over.
  const lockedRef = useRef<string | null>(null);
  const lockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (topicIds.length === 0) return;
    let raf = 0;
    const compute = () => {
      raf = 0;
      // When the page is scrolled to the bottom, the last topic is active even
      // if its section is too short to push its top past the offset line —
      // otherwise selecting a short final topic leaves the previous one lit.
      const atBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      let current = topicIds[topicIds.length - 1];
      if (!atBottom) {
        // Activation line sits ~45% down the viewport so the highlight switches
        // to a topic once its header reaches the upper-middle of the view — not
        // only when it's nearly at the top. `offset` acts as a floor to clear a
        // sticky header on short viewports.
        const line = Math.max(offset, window.innerHeight * 0.45);
        current = topicIds[0];
        for (const id of topicIds) {
          const el = document.getElementById(`topic-${id}`);
          if (el && el.getBoundingClientRect().top - line <= 0) current = id;
        }
      }
      // If a click-scroll is in flight, hold the target until the scroll
      // actually arrives there (then release), ignoring intermediate frames.
      if (lockedRef.current) {
        if (current === lockedRef.current) lockedRef.current = null;
        else return;
      }
      setActiveId(current);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, offset]);

  const scrollToTopic = (id: string) => {
    const el = document.getElementById(`topic-${id}`);
    if (!el) return;
    lockedRef.current = id;
    setActiveId(id);
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    // Safety release in case the scroll never lands exactly on the target
    // (interrupted by the user, sub-pixel rounding, etc.).
    if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
    lockTimerRef.current = setTimeout(() => {
      lockedRef.current = null;
    }, 1000);
  };

  return { activeId, scrollToTopic };
}
